import { Container, Graphics } from 'pixi.js';
import { OBSTACLE_TYPES, type ObstacleConfig } from '../configs';
export class Obstacle extends Container {
  public lane: number;
  public type: string;
  private sprite: Graphics;
  public active: boolean = true;
  private time: number = 0;
  constructor(lane: number, type: keyof typeof OBSTACLE_TYPES) {
    super();
    this.lane = lane;
    this.type = type;
    this.sprite = this.createSprite(OBSTACLE_TYPES[type]);
    this.addChild(this.sprite);
  }
  private createSprite(config: ObstacleConfig): Graphics {
    const graphic = new Graphics();
    if (config.type === 'pothole') {
      // Outer dark rim
      graphic.fill(0x111827);
      graphic.ellipse(0, 0, config.width / 2 + 3, config.height / 2 + 3);
      graphic.fill();
      // Mid rim
      graphic.fill(config.color);
      graphic.ellipse(0, 0, config.width / 2, config.height / 2);
      graphic.fill();
      // Inner hole
      graphic.fill(0x000000);
      graphic.ellipse(0, 2, config.width / 2 - 6, config.height / 2 - 5);
      graphic.fill();
      // Highlight crack
      graphic.stroke({ width: 1.5, color: 0x374151, alpha: 0.9 });
      graphic.moveTo(-config.width / 3, -config.height / 4);
      graphic.lineTo(config.width / 4, 0);
      graphic.lineTo(-config.width / 6, config.height / 4);
      graphic.stroke();
    } else if (config.type === 'barrier') {
      // Shadow
      graphic.fill({ color: 0x000000, alpha: 0.3 });
      graphic.roundRect(-config.width / 2 + 2, -config.height / 2 + 4, config.width, config.height, 4);
      graphic.fill();
      // Main body
      graphic.fill(config.color);
      graphic.roundRect(-config.width / 2, -config.height / 2, config.width, config.height, 4);
      graphic.fill();
      // Top stripe
      graphic.fill(0xffffff);
      graphic.rect(-config.width / 2 + 4, -config.height / 2 + 5, config.width - 8, 6);
      graphic.fill();
      // Bottom stripe
      graphic.fill(0xffffff);
      graphic.rect(-config.width / 2 + 4, config.height / 2 - 11, config.width - 8, 6);
      graphic.fill();
      // Edge highlight
      graphic.stroke({ width: 1.5, color: 0x7f1d1d, alpha: 0.8 });
      graphic.roundRect(-config.width / 2, -config.height / 2, config.width, config.height, 4);
      graphic.stroke();
    } else {
      // Cone - base
      graphic.fill(0x1f2937);
      graphic.roundRect(-config.width / 2 - 2, config.height / 2 - 4, config.width + 4, 8, 2);
      graphic.fill();
      // Triangle body
      graphic.fill(config.color);
      graphic.moveTo(0, -config.height / 2);
      graphic.lineTo(config.width / 2, config.height / 2 - 4);
      graphic.lineTo(-config.width / 2, config.height / 2 - 4);
      graphic.closePath();
      graphic.fill();
      // White stripe 1
      graphic.fill(0xffffff);
      graphic.rect(-config.width / 4, -config.height / 6, config.width / 2, 5);
      graphic.fill();
      // White stripe 2
      graphic.fill(0xffffff);
      graphic.rect(-config.width / 6, config.height / 6, config.width / 3, 5);
      graphic.fill();
      // Top tip
      graphic.fill(0xfbbf24);
      graphic.circle(0, -config.height / 2, 3);
      graphic.fill();
    }
    return graphic;
  }
  update(delta: number): void {
    this.time += delta;
    // Subtle wobble for cones
    if (this.type === 'cone') {
      this.sprite.rotation = Math.sin(this.time * 0.1) * 0.04;
    }
  }
  getBounds(): { x: number; y: number; width: number; height: number } {
    const config = OBSTACLE_TYPES[this.type as keyof typeof OBSTACLE_TYPES];
    return {
      x: this.x - config.width / 2,
      y: this.y - config.height / 2,
      width: config.width,
      height: config.height,
    };
  }
}